import React, { useState, useEffect } from "react";
import "./VideoManager.css";

const API_BASE = "https://api.malidag.com/learn";

const VideoManager = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState("");

  const fetchFiles = async () => {
    try {
      const res = await fetch(`${API_BASE}/videos/list`);
      const data = await res.json();
      setFiles(data.files || []);
    } catch (err) {
      console.error("Error fetching files:", err);
      setMessage("Unable to load files.");
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);

    setLoading(true);
    setMessage("Uploading file...");

    try {
      const res = await fetch(`${API_BASE}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Upload failed");

      await res.json();
      setSelectedFile(null);
      setMessage("File uploaded successfully.");
      fetchFiles();
    } catch (err) {
      console.error("Upload error:", err);
      setMessage("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (fileName) => {
    try {
      const res = await fetch(`${API_BASE}/delete/${fileName}`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Delete failed");

      setMessage("File deleted successfully.");
      fetchFiles();
    } catch (err) {
      console.error("Delete error:", err);
      setMessage("Delete failed.");
    }
  };

  const renderFilePreview = (file) => {
    const url = `https://api.malidag.com${file.url}`;
    const lowerName = file.name.toLowerCase();

    if (lowerName.endsWith(".mp4") || lowerName.endsWith(".webm")) {
      return (
        <video className="video-manager__thumb" src={url} muted />
      );
    }

    if (
      lowerName.endsWith(".png") ||
      lowerName.endsWith(".jpg") ||
      lowerName.endsWith(".jpeg") ||
      lowerName.endsWith(".svg")
    ) {
      return (
        <img
          src={url}
          alt={file.name}
          className="video-manager__thumb"
        />
      );
    }

    if (lowerName.endsWith(".pdf")) {
      return <div className="video-manager__file-icon">PDF</div>;
    }

    return <div className="video-manager__file-icon">FILE</div>;
  };

  return (
    <div className="video-manager">
      <div className="video-manager__header">
        <div>
          <h3 className="video-manager__title">Media Manager</h3>
          <p className="video-manager__subtitle">
            Upload and manage videos, images, and PDFs.
          </p>
        </div>

        <button
          className="video-manager__toggle"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Close" : "Open"}
        </button>
      </div>

      {expanded && (
        <div className="video-manager__body">
          <div className="video-manager__upload">
            <input
              type="file"
              accept="video/mp4,video/webm,image/*,application/pdf"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              className="video-manager__input"
            />

            <button
              onClick={handleUpload}
              disabled={loading || !selectedFile}
              className="video-manager__upload-btn"
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>

          {message && <div className="video-manager__message">{message}</div>}

          <div className="video-manager__list-wrapper">
            {files.length === 0 ? (
              <p className="video-manager__empty">No files uploaded yet.</p>
            ) : (
              <ul className="video-manager__list">
                {files.map((file) => (
                  <li key={file.name} className="video-manager__item">
                    <div className="video-manager__item-left">
                      {renderFilePreview(file)}
                      <span className="video-manager__file-name" title={file.name}>
                        {file.name}
                      </span>
                    </div>

                    <div
                      onClick={() => handleDelete(file.name)}
                      className="video-manager__delete-btn"
                    >
                      Delete
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManager;